import { h, Fragment } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { render } from 'preact';
import { GamesGrid } from './grid';
import { IRecommendationButton, ERecommendationButtonType, IGame } from '../types';
import { Grid, Row } from '@/components/grid';
import { RecommendModalOnCloseParams } from '../index';
import { useOrientation } from '@/hooks/useOrientation';

const RecommendedGuidePanel = (params: {
  games: IGame[];
  title: string;
  buttons: IRecommendationButton[];
  onClose?: (params: RecommendModalOnCloseParams) => void;
}) => {
  const { games, title, buttons, onClose } = params;
  const [shadowRoot, setShadowRoot] = useState<ShadowRoot | null>(null);

  const handleClickBtn = useCallback(
    (btnInfo: IRecommendationButton) => {
      onClose?.({
        type: btnInfo.type === ERecommendationButtonType.Quit ? 'quit' : 'dismiss'
      });
    },
    [onClose]
  );

  const handleGameClick = useCallback(
    (game: IGame) => {
      onClose?.({
        type: 'navigate',
        data: { game }
      });
    },
    [onClose]
  );

  const orientation = useOrientation();

  if (!games.length) {
    return <></>;
  }

  // 创建一个引用DOM元素的回调函数
  const shadowRootRef = useCallback((node: HTMLDivElement | null) => {
    if (node !== null) {
      // 检查是否已经有Shadow DOM
      if (!node.shadowRoot) {
        // 创建Shadow DOM
        const root = node.attachShadow({ mode: 'open' });

        // 创建样式元素
        const style = document.createElement('style');
        style.textContent = `
          .recommend-panel {
            display: flex;
            flex-direction: column;
            background-color: #1a1a1a;
            width: 100%;
            // height: 100%;
          }
          .container {
            // height: 100%;
            display: flex;
            flex-direction: column;
            position: relative;
          }
          .title {
            color: #ffffff;
            font-size: 18px;
            font-weight: 600;
            line-height: 24px;
            text-align: center;
            padding: 20px 10px;
            margin-block-end: 0px;
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            width: 100%;
            max-width: ${orientation === 'portrait' ? '440px' : '75%'};
            margin: 0 auto;
            background-color: #1a1a1a;
            z-index: 1;
            font-family: PingFangSC-Regular;
          }
          .games-container {
            width: 100%;
            display: flex;
            // flex: 1;
            min-height: 120px;  
            overflow: auto;
            margin-bottom: 88px;
            margin-top: 64px;
           
            @media (orientation: landscape) {
              width: 80%;
              margin-bottom: 75px;
              margin-left: auto;
              margin-right: auto;
            }
          }
          .buttons-container {
            position: absolute;
            left: 0;
            right: 0;
            width: calc(100% - 32px);
            max-width: 440px;
            margin: 0 auto;
            background: inherit;
            padding-top: 16px;
            padding-bottom: 16px;
            padding-left: 16px;
            padding-right: 16px;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
          }
          @media (orientation: portrait) {
            .buttons-container {
              bottom: 28px;
            }
          }
          @media (orientation: landscape) {
            .buttons-container {
              bottom: 0;
              width: 80%;
              margin-left: auto;
              margin-right: auto;
            }
          }
          .button-wrapper {
            flex: 0 0 50%;
            box-sizing: border-box;
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .button {
            width: 100%;
            max-width: 160px;
            height: 40px;
            border-radius: 21px;
            white-space: nowrap;
            line-height: 40px;
            padding-left: 8px;
            padding-right: 8px;
            border: 1px solid;
            cursor: pointer;
            flex: 1;
            text-align: center;
            font-size: 16px;
            font-weight: normal;
            outline: none;
            overflow: hidden;
            text-overflow: ellipsis;
          }
            .button-text-ellipsis {
              display: block;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              max-width: 100%; /* 确保文本不会超出按钮边界 */
          }
          .quit-button {
            border-color: #ffffff30;
            background-color: #ffffff2c;
            color: #fff;
          }
          .action-button {
            border-color: #7817FF;
            background-color: #7817FF;
            color: #fff;
          }
        `;

        // 创建内容容器
        const container = document.createElement('div');
        container.id = 'recommend-panel-content';

        // 添加样式和容器到Shadow DOM
        root.appendChild(style);
        root.appendChild(container);

        // 保存shadowRoot引用
        setShadowRoot(root);
      }
    }
  }, []);

  // 使用useEffect在shadowRoot准备好后渲染内容
  useEffect(() => {
    if (shadowRoot) {
      const container = shadowRoot.getElementById('recommend-panel-content');
      if (container) {
        render(
          <div className="recommend-panel">
            <div className="container">
              <p className="title">{title}</p>

              <div className="games-container">
                <GamesGrid
                  list={games || []}
                  routeMode="REDIRECT"
                  layerName="RecommendedGuide"
                  onGameClick={handleGameClick}
                  orientation={orientation}
                />
              </div>

              <div className="buttons-container">
                {(buttons ?? []).map((btnInfo: IRecommendationButton, index: number) => (
                  <div key={index} className="button-wrapper">
                    <button
                      className={`button ${
                        btnInfo.type === ERecommendationButtonType.Quit ? 'quit-button' : 'action-button'
                      }`}
                      onClick={() => handleClickBtn(btnInfo)}
                    >
                      <span className="button-text-ellipsis">{btnInfo.text}</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>,
          container
        );
      }
    }
  }, [shadowRoot, games, title, buttons, handleClickBtn, handleGameClick]);

  return <div ref={shadowRootRef}></div>;
};

export { RecommendedGuidePanel };
